import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import { apiAdmin } from "../../services/api";
import { _imagesURL } from "../../constants/API";
import { CHAT, NEW_PROFILE_DOCTOR } from "../../constants/RoutesUrl";
import { Language } from "../../App";
import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";
import { isImageCached, markImageLoaded } from "../../utils/imageCache";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import {
  IoClose,
} from "react-icons/io5";
import { FaArrowRight, FaChevronRight } from "react-icons/fa";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import {
  Category3DIcon,
  Clock3DIcon,
  Users3DIcon,
  Check3DIcon,
  Edit3DIcon,
  ExperienceShield3DIcon,
  Specialist3DIcon,
  LocationPin3DIcon,
  OneToOne3DIcon,
  ExpertSupport3DIcon,
  CareerGrowth3DIcon,
  Chat3DIcon,
  Fee3DIcon,
} from "../agenda/common/Agenda3DIcons";

// Micro Stat Item (Vibrant Tinted Card)
function StatItem({ icon, label, value, variant = "cyan" }) {
  return (
    <div className={`am-stat-card am-stat-card-${variant}`}>
      <div className="am-stat-icon-wrap">{icon}</div>
      <span className="am-stat-label">{label}</span>
      <span className="am-stat-value" title={value}>
        {value}
      </span>
    </div>
  );
}

// 3-Benefit Highlight Item (Squarish Centered Card)
function BenefitItem({ icon, mainTitle, subTitle, variant = "cyan" }) {
  return (
    <div className={`am-benefit-card am-benefit-card-${variant}`}>
      <div className="am-benefit-icon-wrap">{icon}</div>
      <span className="am-benefit-main">{mainTitle}</span>
      <span className="am-benefit-sub">{subTitle}</span>
    </div>
  );
}

// Compact mentor preview (avatar + name) with "See complete profile" link (Vibrant Bright Card)
function MentorProfileLinkCard({ image, name, onViewProfile }) {
  const { lang } = useContext(Language);
  return (
    <div
      onClick={onViewProfile}
      className="am-profile-strip"
      role={onViewProfile ? "button" : undefined}
      tabIndex={onViewProfile ? 0 : undefined}
      onKeyDown={(e) => {
        if (onViewProfile && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onViewProfile();
        }
      }}
    >
      <div className="am-profile-strip-left">
        <div className="am-profile-strip-avatar-wrap">
          <img
            src={image || placeholderAvatar}
            alt={name}
            className="am-profile-strip-avatar"
            onError={(e) => {
              if (e.target.src !== placeholderAvatar) {
                e.target.src = placeholderAvatar;
              }
            }}
          />
        </div>
        <div className="am-profile-strip-info">
          <span className="am-profile-strip-name" title={name}>
            {name}
          </span>
          <span className="am-profile-strip-action">
            {lang?.mentorship_see_complete_profile || "See complete profile"}
          </span>
        </div>
      </div>
      <span className="am-profile-strip-chevron">
        <FaChevronRight size={12} />
      </span>
    </div>
  );
}

// Shortens street address to just the last two parts (city, country)
function shortenLocation(loc) {
  if (!loc) return "";
  const parts = loc
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.slice(-2).join(", ");
}

function formatDurationParts(minutes, lang) {
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? lang?.min || "min" : lang?.mins || "mins"}`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${hours === 1 ? lang?.hour || "hour" : lang?.hours || "hours"}`;
  }
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? lang?.day || "day" : lang?.days || "days"}`;
}

function formatTimeUntil(request, lang) {
  let targetMs = null;
  if (request?.start_timestamp) {
    targetMs = Number(request.start_timestamp) * 1000;
  } else if (request?.start_date && request?.start_time) {
    const parsed = new Date(`${request.start_date}T${request.start_time}`);
    if (!isNaN(parsed)) targetMs = parsed.getTime();
  }
  if (!targetMs) return null;
  const diffMs = Math.abs(targetMs - Date.now());
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return lang?.mentorship_a_few_moments || "a few moments";
  return formatDurationParts(minutes, lang);
}

function formatElapsedSince(request, lang) {
  let targetMs = null;
  if (request?.start_timestamp) {
    targetMs = Number(request.start_timestamp) * 1000;
  } else if (request?.start_date && request?.start_time) {
    const parsed = new Date(`${request.start_date}T${request.start_time}`);
    if (!isNaN(parsed)) targetMs = parsed.getTime();
  }
  if (!targetMs) return null;
  const diffMs = Math.max(Date.now() - targetMs, 0);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return lang?.just_now || "Just now";
  const timeStr = formatDurationParts(minutes, lang);
  const template = lang?.mentorship_time_ago_template || "{{time}} ago";
  return template.replace("{{time}}", timeStr);
}

async function fetchMentorProfile(userId) {
  const res = await apiAdmin.post("user/mentor/profile", { user_id: userId });
  return res.data;
}

function resolveMentorImage(rawImg) {
  if (
    !rawImg ||
    typeof rawImg !== "string" ||
    ["undefined", "null", "@empty_data_"].includes(String(rawImg).trim().toLowerCase())
  ) {
    return placeholderAvatar;
  }
  const trimmed = rawImg.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("/src/") ||
    trimmed.startsWith("/assets/") ||
    trimmed.startsWith("/static/") ||
    trimmed.startsWith("/@")
  ) {
    return trimmed;
  }
  const cleanPath = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  return `${_imagesURL}/${cleanPath}`;
}

const OPEN_ANIMATION_MS = 800;

function ApplyMentorShip({
  isOpen,
  toggle,
  mentor,
  onApply,
  onViewProfile,
  isBooked,
  onChat,
  mode = "view",
  onManage,
}) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const [profileData, setProfileData] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [fetchStarted, setFetchStarted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || !mentor?.id) {
      setProfileData(null);
      setRequestData(null);
      setLoadingProfile(false);
      setFetchStarted(false);
      setPhotoLoaded(false);
      return;
    }
    let cancelled = false;
    setProfileData(null);
    setRequestData(null);
    setLoadingProfile(false);
    setFetchStarted(false);
    setPhotoLoaded(false);

    const openTimer = setTimeout(() => {
      if (cancelled) return;
      setFetchStarted(true);
      setLoadingProfile(true);
      fetchMentorProfile(mentor.id)
        .then((data) => {
          if (!cancelled && data?.status) {
            setProfileData(data.data?.mentor || null);
            setRequestData(data.data?.request ?? null);
          }
        })
        .catch(() => { })
        .finally(() => {
          if (!cancelled) setLoadingProfile(false);
        });
    }, OPEN_ANIMATION_MS);

    return () => {
      cancelled = true;
      clearTimeout(openTimer);
    };
  }, [isOpen, mentor?.id]);

  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const mentorProfile = profileData?.mentor_profile || {};
  const rawPic =
    mentorProfile.profile_picture ||
    profileData?.image ||
    mentor?.mentor_profile?.profile_picture ||
    mentor?.image;
  const currentImage = mentor ? resolveMentorImage(rawPic) : "";

  useEffect(() => {
    if (currentImage && isImageCached(currentImage)) {
      setPhotoLoaded(true);
    }
  }, [currentImage]);

  if (!mentor) return null;
  if (!isOpen && !isClosing) return null;

  const isOwnProfile = mode === "own";
  const hasRequest = isBooked || !!requestData;

  const display = profileData
    ? {
      image: currentImage,
      name: profileData.name || mentor.name,
      specialty:
        mentorProfile.specialty ||
        profileData.category ||
        profileData.subcategory ||
        mentor.specialty,
      bio: mentorProfile.bio || profileData.bio || mentor.bio,
      institution: mentorProfile.institute || mentor.institution,
      institutionSub: undefined,
      location: shortenLocation(profileData.location) || mentor.location,
      experience: mentorProfile.experience || mentor.experience,
      focusAreas: mentorProfile.focus
        ? mentorProfile.focus.split("&&&&").filter(Boolean)
        : mentor.focusAreas || [],
      price: mentorProfile.price ?? mentor.mentor_profile?.price ?? mentor.price,
    }
    : {
      ...mentor,
      image: currentImage,
    };

  const {
    image,
    name,
    specialty,
    bio,
    institution,
    location,
    experience,
    focusAreas = [],
    price,
  } = display;
  const priceValue = Number(price) || 0;

  const handleApply = () => closeThen(() => onApply?.(mentor));

  const handleViewFullProfile = () =>
    closeThen(() => {
      onViewProfile?.(mentor);
      navigate(`${NEW_PROFILE_DOCTOR}/${mentor.user_name || mentor.id}`);
    });

  const handleChat = () => {
    const loginId = JSON.parse(localStorage.getItem("SplashData") || "null")
      ?.user?.id;
    closeThen(() => {
      onChat?.(mentor);
      navigate(CHAT, {
        state: {
          from: loginId,
          to: mentor.id,
          group_id: 0,
          user: {
            user_id: mentor.id,
            name: display.name,
            image: "",
          },
        },
      });
    });
  };

  const handleManage = () => closeThen(() => onManage?.(mentor));

  return (
    <div
      className={`am-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        .am-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: amBackdropFadeIn 0.8s ease-out forwards;
        }
        .am-curtain-backdrop.closing {
          animation: amBackdropFadeOut 0.8s ease-out forwards;
        }
        .am-curtain-content {
          position: relative;
          width: 100%;
          max-width: 610px;
          max-height: 88vh;
          display: flex;
          flex-direction: column;
          background: #FFFFFF;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          transform: translateY(-100vh);
          animation: amCurtainOpen 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
          overflow: hidden;
        }
        .am-curtain-content.closing {
          animation: amCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes amBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes amBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes amCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes amCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .am-curtain-backdrop, .am-curtain-content {
            animation: none !important;
          }
        }
        
        /* Pinned / Sticky Top Hero Header */
        .am-modal-header-sticky {
          position: relative;
          flex-shrink: 0;
          padding: 20px 24px 12px;
          z-index: 15;
          background: #FFFFFF;
        }

        /* Modal Scrollable Body with Clean Hidden Scrollbars */
        .am-modal-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 2px 24px 16px;
          background: #FFFFFF;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .am-modal-body::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        
        /* Modern Glass Close Button */
        .am-close-btn {
          position: absolute;
          top: 18px;
          right: 20px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid #BCEAF2;
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0E7490;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 30;
        }
        .am-close-btn:hover {
          background: #00BADA;
          color: #FFFFFF;
          border-color: #00BADA;
          transform: rotate(90deg) scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 186, 218, 0.35);
        }

        /* -------------------------------------------------------------
           1. Top Hero Profile Section
           ------------------------------------------------------------- */
        .am-hero-wrap {
          display: flex;
          align-items: center;
          gap: 16px;
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
          box-shadow: none;
          margin-bottom: 0;
        }
        .am-hero-photo-halo {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 18px;
          padding: 3px;
          background: linear-gradient(135deg, #00F0FF 0%, #00BADA 50%, #38BDF8 100%);
          box-shadow: 0 6px 20px rgba(0, 186, 218, 0.35);
          flex-shrink: 0;
          cursor: pointer;
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .am-hero-photo-halo:hover {
          transform: scale(1.03);
          box-shadow: 0 10px 28px rgba(0, 186, 218, 0.55);
        }
        .am-hero-photo-halo:hover .am-hero-photo-zoom-hint {
          opacity: 1;
        }
        .am-hero-photo-zoom-hint {
          position: absolute;
          inset: 0;
          border-radius: 15px;
          background: rgba(0, 186, 218, 0.32);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #FFFFFF;
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
          z-index: 2;
        }
        .am-hero-photo-wrap {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 15px;
          overflow: hidden;
          background: #EAF8FB;
          border: 2px solid #FFFFFF;
        }
        .am-hero-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .am-hero-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding-right: 36px;
        }
        .am-specialty-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, rgba(0, 186, 218, 0.2) 0%, rgba(8, 145, 178, 0.14) 100%);
          color: #0891B2;
          border: 1px solid rgba(0, 186, 218, 0.4);
          font-size: 11.5px;
          font-weight: 800;
          letter-spacing: 0.3px;
          border-radius: 20px;
          padding: 3px 11px;
          width: fit-content;
          margin-bottom: 5px;
        }
        .am-mentor-name {
          font-size: 21px;
          font-weight: 800;
          color: #0F1A2B;
          letter-spacing: -0.02em;
          margin: 0 0 6px 0;
          line-height: 1.25;
        }
        .am-institution-row {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #475569;
          font-size: 12.5px;
          background: rgba(255, 255, 255, 0.82);
          border: 1px solid rgba(0, 186, 218, 0.3);
          border-radius: 12px;
          padding: 7px 12px;
        }
        .am-institution-icon {
          color: #00BADA;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .am-institution-text {
          min-width: 0;
          line-height: 1.3;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .am-institution-label {
          font-size: 9.5px;
          font-weight: 800;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: block;
          margin-bottom: 1px;
        }
        .am-institution-value {
          font-weight: 700;
          color: #0F1A2B;
          display: block;
        }

        /* -------------------------------------------------------------
           2. Summary Stats 4-Column Grid (Rich Color Tinted Cards)
           ------------------------------------------------------------- */
        .am-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          margin-bottom: 12px;
        }
        .am-stat-card {
          border-radius: 14px;
          padding: 12px 8px 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          min-width: 0;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.08);
        }
        .am-stat-card-cyan {
          background: linear-gradient(145deg, #E0F7FA 0%, #D0F2F8 100%);
          border: 1.5px solid #9BE5F3;
        }
        .am-stat-card-cyan:hover {
          border-color: #00BADA;
          background: linear-gradient(145deg, #D4F4F9 0%, #C4EFF7 100%);
          box-shadow: 0 6px 18px rgba(0, 186, 218, 0.22);
          transform: translateY(-2px);
        }
        .am-stat-card-sky {
          background: linear-gradient(145deg, #E0F2FE 0%, #D0EAFA 100%);
          border: 1.5px solid #A5D8FC;
        }
        .am-stat-card-sky:hover {
          border-color: #38BDF8;
          background: linear-gradient(145deg, #D4ECFC 0%, #C2E3FA 100%);
          box-shadow: 0 6px 18px rgba(14, 165, 233, 0.22);
          transform: translateY(-2px);
        }
        .am-stat-card-indigo {
          background: linear-gradient(145deg, #EEF2FF 0%, #E0E7FF 100%);
          border: 1.5px solid #C7D2FE;
        }
        .am-stat-card-indigo:hover {
          border-color: #6366F1;
          background: linear-gradient(145deg, #E5EAFF 0%, #D6DFFF 100%);
          box-shadow: 0 6px 18px rgba(99, 102, 241, 0.22);
          transform: translateY(-2px);
        }
        .am-stat-card-violet {
          background: linear-gradient(145deg, #FAF5FF 0%, #F3E8FF 100%);
          border: 1.5px solid #E9D5FF;
        }
        .am-stat-card-violet:hover {
          border-color: #A855F7;
          background: linear-gradient(145deg, #F5EBFF 0%, #EEDBFF 100%);
          box-shadow: 0 6px 18px rgba(168, 85, 247, 0.22);
          transform: translateY(-2px);
        }
        .am-stat-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 5px;
        }
        .am-stat-label {
          font-size: 10px;
          font-weight: 800;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: block;
          margin-bottom: 2px;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .am-stat-value {
          font-size: 13.5px;
          font-weight: 800;
          color: #0F1A2B;
          display: block;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* -------------------------------------------------------------
           3. Card Sections (About & Focus Area)
           ------------------------------------------------------------- */
        .am-card-section {
          background: linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%);
          border: 1.5px solid #B4E8F2;
          border-radius: 16px;
          padding: 13px 16px;
          margin-bottom: 12px;
          box-shadow: 0 2px 10px rgba(0, 186, 218, 0.08);
          transition: all 0.2s ease;
        }
        .am-card-section:hover {
          border-color: #00BADA;
          box-shadow: 0 4px 16px rgba(0, 186, 218, 0.15);
        }
        .am-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .am-section-header-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .am-section-title {
          font-size: 14px;
          font-weight: 800;
          color: #0F1A2B;
          letter-spacing: -0.01em;
        }
        .am-section-badge {
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 100%);
          color: #FFFFFF;
          font-size: 11px;
          font-weight: 800;
          padding: 2px 9px;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0, 186, 218, 0.3);
        }
        .am-about-text {
          font-size: 12.5px;
          line-height: 1.55;
          color: #2B3B4E;
          margin: 0;
          word-break: break-word;
          overflow-wrap: break-word;
        }

        /* Focus Area Chips Grid (Rich Tinted Cyan Chips) */
        .am-focus-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
        }
        .am-focus-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: linear-gradient(135deg, #E0F7FA 0%, #D0F1F7 100%);
          border: 1.5px solid #00BADA;
          border-radius: 12px;
          padding: 7px 13px;
          color: #065F70;
          font-size: 12px;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.14);
          transition: all 0.2s ease;
          max-width: 100%;
        }
        .am-focus-chip:hover {
          background: linear-gradient(135deg, #D2F3F8 0%, #C0EBF4 100%);
          border-color: #008AA8;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 186, 218, 0.22);
        }
        .am-focus-text {
          white-space: normal;
          word-break: break-word;
        }

        /* -------------------------------------------------------------
           4. Highlights / Benefits 3-Column Grid (Squarish Centered Cards)
           ------------------------------------------------------------- */
        .am-benefits-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 12px;
        }
        .am-benefit-card {
          border-radius: 16px;
          padding: 13px 8px 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          justify-content: center;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.08);
          min-width: 0;
        }
        .am-benefit-card-cyan {
          background: linear-gradient(145deg, #E0F7FA 0%, #D0F2F8 100%);
          border: 1.5px solid #9BE5F3;
        }
        .am-benefit-card-sky {
          background: linear-gradient(145deg, #E0F2FE 0%, #D0EAFA 100%);
          border: 1.5px solid #A5D8FC;
        }
        .am-benefit-card-indigo {
          background: linear-gradient(145deg, #EEF2FF 0%, #E0E7FF 100%);
          border: 1.5px solid #C7D2FE;
        }
        .am-benefit-card:hover {
          border-color: #00BADA;
          box-shadow: 0 6px 18px rgba(0, 186, 218, 0.22);
          transform: translateY(-2px);
        }
        .am-benefit-card-indigo:hover {
          border-color: #6366F1;
          box-shadow: 0 6px 18px rgba(99, 102, 241, 0.22);
        }
        .am-benefit-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 7px;
        }
        .am-benefit-main {
          font-size: 13px;
          font-weight: 850;
          color: #0F1A2B;
          display: block;
          margin-bottom: 3px;
          line-height: 1.25;
          letter-spacing: -0.01em;
          width: 100%;
        }
        .am-benefit-sub {
          font-size: 10.5px;
          font-weight: 650;
          color: #0E7490;
          display: block;
          line-height: 1.3;
          width: 100%;
        }
        .am-benefit-card-sky .am-benefit-sub {
          color: #0284C7;
        }
        .am-benefit-card-indigo .am-benefit-sub {
          color: #4F46E5;
        }

        /* -------------------------------------------------------------
           5. Secondary Profile Strip Card (Vibrant Bright Colors)
           ------------------------------------------------------------- */
        .am-profile-strip {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #CEF4FA 0%, #A2ECF8 45%, #75E1F4 100%);
          border: 2px solid #00BADA;
          border-radius: 16px;
          padding: 12px 16px;
          margin-bottom: 12px;
          box-shadow: 0 4px 16px rgba(0, 186, 218, 0.28), 0 0 0 1px rgba(0, 200, 232, 0.15);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .am-profile-strip::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 120px;
          height: 120px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.45) 0%, transparent 70%);
          pointer-events: none;
        }
        .am-profile-strip:hover {
          border-color: #008AA8;
          background: linear-gradient(135deg, #BCEFF8 0%, #90E6F6 45%, #60DBF2 100%);
          box-shadow: 0 8px 26px rgba(0, 186, 218, 0.42), 0 0 0 1.5px rgba(0, 186, 218, 0.4);
          transform: translateY(-2px);
        }
        .am-profile-strip-left {
          display: flex;
          align-items: center;
          gap: 13px;
          min-width: 0;
          z-index: 1;
        }
        .am-profile-strip-avatar-wrap {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          padding: 2.5px;
          background: linear-gradient(135deg, #00F0FF 0%, #00BADA 50%, #0284C7 100%);
          box-shadow: 0 4px 12px rgba(0, 186, 218, 0.4);
          flex-shrink: 0;
        }
        .am-profile-strip-avatar {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          border: 2px solid #FFFFFF;
        }
        .am-profile-strip-info {
          min-width: 0;
          line-height: 1.35;
        }
        .am-profile-strip-name {
          font-size: 14.5px;
          font-weight: 850;
          color: #063943;
          display: block;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: -0.01em;
        }
        .am-profile-strip-action {
          font-size: 12.5px;
          font-weight: 750;
          color: #006D85;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }
        .am-profile-strip:hover .am-profile-strip-action {
          color: #024554;
          text-decoration: underline;
        }
        .am-profile-strip-chevron {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 100%);
          color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 3px 10px rgba(0, 186, 218, 0.45);
          transition: all 0.25s ease;
          border: 1.5px solid rgba(255, 255, 255, 0.6);
          z-index: 1;
        }
        .am-profile-strip:hover .am-profile-strip-chevron {
          background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 100%);
          transform: translateX(3px) scale(1.08);
          box-shadow: 0 6px 16px rgba(0, 186, 218, 0.6);
        }

        /* -------------------------------------------------------------
           6. Refined Status / Info Panels (Aqua Cyan - 1 Line Optimized)
           ------------------------------------------------------------- */
        .am-status-panel {
          display: flex;
          align-items: center;
          gap: 10px;
          border-radius: 12px;
          padding: 9px 14px;
          margin-bottom: 6px;
        }
        /* Requested / Pending: Fresh vibrant Aqua Cyan (Completely replaces yellow) */
        .am-status-panel-aqua {
          background: linear-gradient(135deg, #E0F8FA 0%, #C6F1F8 100%);
          border: 1.5px solid #00BADA;
          box-shadow: 0 4px 16px rgba(0, 186, 218, 0.18);
        }
        /* Scheduled / In-Progress */
        .am-status-panel-sky {
          background: linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%);
          border: 1.5px solid #38BDF8;
          box-shadow: 0 4px 16px rgba(14, 165, 233, 0.18);
        }
        .am-status-icon-wrap {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .am-status-text {
          font-size: 12px;
          line-height: 1.35;
          margin: 0;
          min-width: 0;
          font-weight: 600;
        }
        .am-status-text-aqua {
          color: #064E5B;
        }
        .am-status-text-sky {
          color: #0369A1;
        }

        /* -------------------------------------------------------------
           7. Sticky Footer & Modern High-End CTA Buttons
           ------------------------------------------------------------- */
        .am-modal-footer-sticky {
          flex-shrink: 0;
          padding: 12px 24px 16px;
          background: #FFFFFF;
          border-top: 1px solid #EDF2F7;
          box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
          z-index: 20;
        }
        .am-cta-btn {
          width: 100%;
          height: 48px;
          border-radius: 999px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 750;
          letter-spacing: 0.2px;
          color: #FFFFFF;
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 50%, #0088B3 100%);
          box-shadow: 0 6px 22px rgba(0, 186, 218, 0.4);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .am-cta-btn:hover {
          background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 50%, #0099C8 100%);
          box-shadow: 0 8px 26px rgba(0, 186, 218, 0.55);
          transform: translateY(-1.5px);
        }
        .am-cta-btn:active {
          transform: translateY(0.5px);
          box-shadow: 0 4px 14px rgba(0, 186, 218, 0.28);
        }

        /* Responsive Breakpoints */
        @media (max-width: 768px) {
          .am-modal-body {
            padding: 18px 18px 12px;
          }
          .am-modal-footer-sticky {
            padding: 10px 18px 14px;
          }
        }
        @media (max-width: 576px) {
          .am-curtain-content {
            border-radius: 20px;
            max-height: 92vh;
          }
          .am-modal-header-sticky {
            padding: 16px 14px 10px;
          }
          .am-modal-body {
            padding: 2px 14px 12px;
          }
          .am-modal-footer-sticky {
            padding: 10px 14px 14px;
          }
          .am-hero-wrap {
            gap: 12px;
          }
          .am-hero-photo-halo {
            width: 100px;
            height: 100px;
          }
          .am-hero-info {
            padding-right: 28px;
          }
          .am-mentor-name {
            font-size: 18px;
          }
          .am-institution-row {
            padding: 6px 10px;
            font-size: 11.5px;
          }
          .am-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          .am-benefits-row {
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
          }
          .am-cta-btn {
            height: 46px;
            font-size: 14px;
          }
        }
        @media (max-width: 440px) {
          .am-hero-wrap {
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 10px;
          }
          .am-hero-photo-halo {
            width: 95px;
            height: 95px;
            margin: 0 auto;
          }
          .am-hero-info {
            padding-right: 0;
            align-items: center;
          }
          .am-institution-row {
            justify-content: center;
            text-align: center;
          }
          .am-benefits-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
        }
      `}</style>

      <div
        className={`am-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="am-close-btn"
          aria-label="Close modal"
        >
          <IoClose size={18} />
        </button>

        {!fetchStarted || loadingProfile ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: "440px", width: "100%" }}
          >
            <Spinner
              style={{
                color: "#00BADA",
                width: "1rem",
                height: "1rem",
                borderWidth: "1px",
              }}
            />
          </div>
        ) : (
          <>
            {/* Pinned Sticky Top Header: Hero Profile Info (Never scrolls) */}
            <div className="am-modal-header-sticky">
              <div className="am-hero-wrap">
                <PhotoProvider
                  speed={() => 500}
                  maskOpacity={0.88}
                  bannerVisible={false}
                >
                  <PhotoView src={image || placeholderAvatar}>
                    <div
                      className="am-hero-photo-halo"
                      role="button"
                      tabIndex={0}
                      title={lang?.click_to_view_photo || "Click to preview photo"}
                    >
                      <div className="am-hero-photo-wrap">
                        {!photoLoaded && (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 1,
                            }}
                          >
                            <Spinner size="sm" style={{ color: "#00BADA" }} />
                          </div>
                        )}
                        <img
                          src={image || placeholderAvatar}
                          alt={name}
                          className="am-hero-photo"
                          loading="eager"
                          decoding="async"
                          onLoad={() => {
                            markImageLoaded(image);
                            setPhotoLoaded(true);
                          }}
                          onError={(e) => {
                            markImageLoaded(image);
                            setPhotoLoaded(true);
                            if (e.target.src !== placeholderAvatar) {
                              e.target.src = placeholderAvatar;
                            }
                          }}
                          style={{
                            opacity: photoLoaded ? 1 : 0,
                            transition: photoLoaded ? "none" : "opacity 0.25s ease-out",
                            willChange: "opacity",
                          }}
                        />
                        <div className="am-hero-photo-zoom-hint">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="#FFFFFF" strokeWidth="2.2" />
                            <path d="M11 8V14M8 11H14" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
                            <path d="M16 16L21 21" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </PhotoView>
                </PhotoProvider>

                <div className="am-hero-info">
                  {specialty && (
                    <span className="am-specialty-badge">
                      <Specialist3DIcon size={15} />
                      {specialty}
                    </span>
                  )}
                  <h4 className="am-mentor-name">{name}</h4>
                  {institution && (
                    <div className="am-institution-row">
                      <HiOutlineBuildingOffice2
                        size={19}
                        className="am-institution-icon"
                      />
                      <div className="am-institution-text">
                        <span className="am-institution-label">
                          {lang?.institution || "Institution"}
                        </span>
                        <span className="am-institution-value">
                          {institution}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Content Body (Stats, About, Focus Areas, Highlights, etc.) */}
            <div className="am-modal-body position-relative">
              {/* 2. Summary Stats 4-Item Grid (Rich Color Tinted Cards) */}
              <div className="am-stats-grid">
                <StatItem
                  variant="cyan"
                  icon={<Specialist3DIcon size={24} />}
                  label={lang?.mentorship_specialist_label || "Specialist"}
                  value={specialty || "—"}
                />

                <StatItem
                  variant="sky"
                  icon={<LocationPin3DIcon size={24} />}
                  label={lang?.location || "Location"}
                  value={location || "—"}
                />

                <StatItem
                  variant="indigo"
                  icon={<ExperienceShield3DIcon size={24} />}
                  label={lang?.experience || "Experience"}
                  value={experience || "—"}
                />

                <StatItem
                  variant="violet"
                  icon={<Fee3DIcon size={24} />}
                  label={lang?.mentorship_price_label || "Price"}
                  value={
                    priceValue > 0
                      ? `€${priceValue.toFixed(2)}`
                      : lang?.free || "Free"
                  }
                />
              </div>

              {/* 3. About Section (Rich Tinted Card) */}
              {bio && (
                <div className="am-card-section">
                  <div className="am-section-header">
                    <div className="am-section-header-left">
                      <Users3DIcon size={20} />
                      <span className="am-section-title">
                        {lang?.mentorship_about_label || "About"}
                      </span>
                    </div>
                  </div>
                  <p className="am-about-text">{bio}</p>
                </div>
              )}

              {/* 4. Focus Area Section (Rich Tinted Cyan Chips - NOT WHITE!) */}
              {focusAreas.length > 0 && (
                <div className="am-card-section">
                  <div className="am-section-header">
                    <div className="am-section-header-left">
                      <Category3DIcon size={20} />
                      <span className="am-section-title">
                        {lang?.mentorship_focus_area_label || "Focus Area"}
                      </span>
                    </div>
                  </div>
                  <div className="am-focus-grid">
                    {focusAreas.map((item) => (
                      <div className="am-focus-chip" key={item} title={item}>
                        <Check3DIcon size={15} />
                        <span className="am-focus-text">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Benefits / Highlights Row (Squarish Centered Cards) */}
              <div className="am-benefits-row">
                <BenefitItem
                  variant="cyan"
                  icon={<OneToOne3DIcon size={32} />}
                  mainTitle={
                    lang?.mentorship_benefit_guidance_sub || "1:1 Mentorship"
                  }
                  subTitle={
                    lang?.mentorship_benefit_guidance_title ||
                    "Personal Guidance"
                  }
                />

                <BenefitItem
                  variant="sky"
                  icon={<ExpertSupport3DIcon size={32} />}
                  mainTitle={
                    lang?.mentorship_benefit_experience_sub || "Expert Support"
                  }
                  subTitle={
                    lang?.mentorship_benefit_experience_title ||
                    "Learn from experience"
                  }
                />

                <BenefitItem
                  variant="indigo"
                  icon={<CareerGrowth3DIcon size={32} />}
                  mainTitle={
                    lang?.mentorship_benefit_goals_sub || "Career Growth"
                  }
                  subTitle={
                    lang?.mentorship_benefit_goals_title || "Achieve your goals"
                  }
                />
              </div>

              {/* 6. Secondary Profile Strip (Vibrant Bright Card with Avatar Halo & Cyan Chevron) */}
              {!isOwnProfile && (
                <MentorProfileLinkCard
                  image={image}
                  name={name}
                  onViewProfile={handleViewFullProfile}
                />
              )}

              {/* 7. Status / Info Panels (Aqua Cyan - 1 Line Optimized) */}
              {hasRequest &&
                !isOwnProfile &&
                (requestData?.status === 2 ? (
                  <div className="am-status-panel am-status-panel-aqua">
                    <div className="am-status-icon-wrap">
                      <Clock3DIcon size={20} />
                    </div>
                    <p className="am-status-text am-status-text-aqua">
                      {(() => {
                        const template =
                          lang?.mentorship_session_in_progress_desc ||
                          "Your mentorship session with this mentor is in progress";
                        const [prefix, suffix] = template.split("{{time}}");
                        const timeValue =
                          formatElapsedSince(requestData, lang) ||
                          lang?.mentorship_recently_fallback ||
                          "recently";
                        return (
                          <>
                            {prefix}
                            <strong style={{ fontWeight: 750 }}>
                              {timeValue}
                            </strong>
                            {suffix}
                          </>
                        );
                      })()}
                    </p>
                  </div>
                ) : requestData?.status === 1 ? (
                  <div className="am-status-panel am-status-panel-sky">
                    <div className="am-status-icon-wrap">
                      <Clock3DIcon size={20} />
                    </div>
                    <p className="am-status-text am-status-text-sky">
                      {(() => {
                        const template =
                          lang?.mentorship_session_scheduled_desc ||
                          "Your consultation with this mentor is scheduled in {{time}} — we'll notify you before it starts.";
                        const [prefix, suffix] = template.split("{{time}}");
                        const timeValue =
                          formatTimeUntil(requestData, lang) ||
                          lang?.mentorship_soon_fallback ||
                          "soon";
                        return (
                          <>
                            {prefix}
                            <strong style={{ fontWeight: 750 }}>
                              {timeValue}
                            </strong>
                            {suffix}
                          </>
                        );
                      })()}
                    </p>
                  </div>
                ) : (
                  <div className="am-status-panel am-status-panel-aqua">
                    <div className="am-status-icon-wrap">
                      <Clock3DIcon size={20} />
                    </div>
                    <p className="am-status-text am-status-text-aqua">
                      {lang?.mentorship_request_sent_desc ||
                        "Request sent to mentor. We'll notify you once your session is confirmed."}
                    </p>
                  </div>
                ))}
            </div>

            {/* 8. Pinned Sticky Bottom Footer with Main Action Button */}
            <div className="am-modal-footer-sticky">
              {isOwnProfile ? (
                <button
                  type="button"
                  onClick={handleManage}
                  className="am-cta-btn"
                >
                  <Edit3DIcon size={18} />
                  <span>
                    {lang?.mentorship_manage_profile_button ||
                      "Manage Mentor Profile"}
                  </span>
                </button>
              ) : hasRequest ? (
                <button
                  type="button"
                  onClick={handleChat}
                  className="am-cta-btn"
                >
                  <Chat3DIcon size={22} />
                  <span>
                    {lang?.mentorship_chat_with_mentor_button ||
                      "Chat with Mentor"}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleApply}
                  className="am-cta-btn"
                >
                  <span>
                    {lang?.mentorship_apply_for_mentorship_button ||
                      "Apply for Mentorship"}
                  </span>
                  <FaArrowRight size={13} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(ApplyMentorShip);
